import { useUserData } from '@contexts/UserDataContext'
import { createInitialChallenge, getSurahVerseCount, surahVerseToGlobalVerse } from 'helpers/readingChallengeHelpers'
import surahsOfQuran from 'data/surahNames'
import { useState } from 'react'

const GOAL_PRESETS = [
  { value: 1, label: 'Ayet / gün' },
  { value: 3, label: 'Ayet / gün' },
  { value: 5, label: 'Ayet / gün' },
  { value: 10, label: 'Ayet / gün' },
]

interface GoalSetupScreenProps {
  onComplete?: () => void
}

const GoalSetupScreen = ({ onComplete }: GoalSetupScreenProps) => {
  const { userData, updateUserData } = useUserData()
  
  const existingChallenge = userData.readingChallenge

  const [selectedGoal, setSelectedGoal] = useState<number | null>(
    existingChallenge?.dailyGoal || null
  )
  const [selectedSurah, setSelectedSurah] = useState(
    existingChallenge?.startingSurah || 1
  )
  const [selectedVerse, setSelectedVerse] = useState<number | ''>(
    existingChallenge?.startingVerse || 1
  )

  const maxVerse = getSurahVerseCount(selectedSurah)

  const handleSurahChange = (surahId: number) => {
    setSelectedSurah(surahId)
    setSelectedVerse(1) // Reset verse when surah changes
  }

  const handleSubmit = () => {
    if (!selectedGoal) return

    const normalizedVerse = Number(selectedVerse) || 1

    if (existingChallenge) {
      // Preserve history, just update the goal and position
      const updatedChallenge = {
        ...existingChallenge,
        dailyGoal: selectedGoal,
        startingSurah: selectedSurah,
        startingVerse: normalizedVerse,
        currentVerse: surahVerseToGlobalVerse(selectedSurah, normalizedVerse),
      }
      updateUserData('readingChallenge', updatedChallenge)
    } else {
      // Create new challenge
      const challenge = createInitialChallenge(
        selectedGoal,
        selectedSurah,
        normalizedVerse,
      )
      updateUserData('readingChallenge', challenge)
    }
    
    if (onComplete) {
      onComplete()
    }
  }

  return (
    <div className="min-h-screen bg-main flex flex-col items-center px-6 py-12">
      <div className="w-full max-w-sm">
        {/* Title */}
        <h1 className="text-2xl font-bold text-main text-center mb-2">
          Günlük Hedefin
        </h1>
        <p className="text-muted text-center text-sm mb-8">
          Günde kaç ayet okumak istiyorsun?
        </p>

        {/* Goal preset cards */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {GOAL_PRESETS.map((preset) => (
            <button
              key={preset.value}
              onClick={() => setSelectedGoal(preset.value)}
              className={`flex flex-col items-center justify-center p-4 sm:p-5 rounded-2xl border-2 transition-all duration-300 active:scale-95 ${
                selectedGoal === preset.value
                  ? 'border-brand-secondary bg-brand-secondary-subtle sacred-shadow scale-[1.02]'
                  : 'border-main bg-surface-subtle hover:border-brand-secondary/30'
              }`}
            >
              <span
                className={`text-3xl font-bold mb-1 transition-colors duration-300 ${
                  selectedGoal === preset.value
                    ? 'text-brand-secondary'
                    : 'text-main'
                }`}
              >
                {preset.value}
              </span>
              <span className={`text-xs font-medium transition-colors duration-300 ${
                selectedGoal === preset.value ? 'text-brand-secondary/80' : 'text-muted'
              }`}>
                {preset.label}
              </span>
            </button>
          ))}
        </div>

        {/* Custom goal input */}
        <div className="mb-8">
          <label htmlFor="custom-goal-input" className="text-muted text-xs font-medium mb-1 block">
            Veya kendi hedefini belirle (Ayet / gün)
          </label>
          <input
            id="custom-goal-input"
            type="number"
            min={1}
            value={selectedGoal || ''}
            onChange={(e) => setSelectedGoal(e.target.value ? Number(e.target.value) : null)}
            placeholder="Örn: 20"
            className={`w-full bg-surface-subtle text-main border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-secondary/50 text-sm transition-all ${
              selectedGoal && !GOAL_PRESETS.find(p => p.value === selectedGoal)
                ? 'border-brand-secondary ring-1 ring-brand-secondary/30'
                : 'border-main'
            }`}
          />
        </div>

        {/* Starting position */}
        <div className="mb-8">
          <h2 className="font-semibold text-main text-base mb-1">
            Nereden başlamak istiyorsun?
          </h2>
          <p className="text-muted text-xs mb-4">
            Kaldığınız yerden devam edebilirsiniz
          </p>

          {/* Surah dropdown */}
          <div className="mb-3">
            <label htmlFor="surah-select" className="text-muted text-xs font-medium mb-1 block">
              Sure
            </label>
            <select
              id="surah-select"
              value={selectedSurah}
              onChange={(e) => handleSurahChange(Number(e.target.value))}
              className="w-full bg-surface-subtle text-main border border-main rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-secondary/50 appearance-none text-sm"
            >
              {surahsOfQuran.map((name, index) => (
                <option key={index} value={index + 1}>
                  {index + 1}. {name}
                </option>
              ))}
            </select>
          </div>

          {/* Verse number input */}
          <div>
            <label htmlFor="verse-input" className="text-muted text-xs font-medium mb-1 block">
              Ayet ({maxVerse} ayete kadar)
            </label>
            <input
              id="verse-input"
              type="number"
              min={1}
              max={maxVerse}
              value={selectedVerse}
              onChange={(e) =>
                setSelectedVerse(
                  e.target.value === ''
                    ? ''
                    : Math.min(Number(e.target.value), maxVerse)
                )
              }
              className="w-full bg-surface-subtle text-main border border-main rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-secondary/50 text-sm"
            />
          </div>
        </div>

        {/* Motivational hadith */}
        <div className="border-2 border-dashed border-brand-secondary/30 rounded-2xl p-5 mb-8 text-center">
          <div className="text-3xl mb-3">💕</div>
          <p className="text-main text-sm leading-relaxed">
            Allah&apos;ın en <strong>sevdiği</strong> amel, az da olsa{' '}
            <strong className="italic">devamlı</strong> olanıdır.
          </p>
        </div>

        {/* Continue button */}
        <button
          onClick={handleSubmit}
          disabled={!selectedGoal}
          className="w-full py-4 rounded-2xl font-bold text-base transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed bg-brand-secondary text-white hover:brightness-110 shadow-lg shadow-brand-secondary/20"
        >
          Devam Et
        </button>
      </div>
    </div>
  )
}

export default GoalSetupScreen
